import Phaser from "phaser";
import type { EventBus, MinigameDelta } from "../engine/EventBus";
import type { Blackboard } from "../engine/Blackboard";
import { MinigameHost, type MinigameModule } from "./MinigameHost";
import { MINIGAME_REGISTRY } from "./minigameRegistry";

const DEFAULT_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

export interface MinigameSceneData {
  bus: EventBus;
  bb: Blackboard;
  id: string;
  config: Record<string, unknown>;
}

export class MinigameScene extends Phaser.Scene {
  private _minigameId = "";
  private _module: MinigameModule | null = null;
  private _host: MinigameHost | null = null;
  private _bus!: EventBus;
  private _bb!: Blackboard;
  private _config: Record<string, unknown> = {};
  private _watchdog: Phaser.Time.TimerEvent | null = null;
  private _completed = false;
  private _unsubComplete: (() => void) | null = null;

  constructor() {
    super({ key: "MinigameScene" });
  }

  init(data: MinigameSceneData): void {
    this._bus = data.bus;
    this._bb = data.bb;
    this._minigameId = data.id;
    this._config = data.config;
    this._completed = false;
    this._module = null;
    this._host = null;
  }

  create(): void {
    const timeoutMs = (this._config.timeoutMs as number | undefined) ?? DEFAULT_TIMEOUT_MS;

    this._unsubComplete = this._bus.on("minigame.complete", ({ id }) => {
      if (id === this._minigameId && !this._completed) {
        this._completed = true;
        this._shutdown();
      }
    });

    this._host = new MinigameHost(
      this,
      this._bus,
      this._bb,
      this._minigameId,
      (result, delta) => this._onComplete(result, delta),
    );

    this._watchdog = this.time.delayedCall(timeoutMs, () => {
      console.warn(`[MinigameScene] ${this._minigameId} timed out`);
      this._forceComplete("cancelled", "timeout");
    });

    const loader = MINIGAME_REGISTRY[this._minigameId];
    if (!loader) {
      console.error(`[MinigameScene] unknown minigame: "${this._minigameId}"`);
      this._forceComplete("cancelled", `unknown minigame: ${this._minigameId}`);
      return;
    }

    loader()
      .then((mod) => {
        if (this._completed) return;
        this._module = mod.default;
        try {
          this._module.create(this._host!, this._config);
        } catch (err) {
          console.error(`[MinigameScene] crash in ${this._minigameId}.create():`, err);
          this._forceComplete("cancelled", String(err));
        }
      })
      .catch((err) => {
        console.error(`[MinigameScene] failed to load ${this._minigameId}:`, err);
        this._forceComplete("cancelled", String(err));
      });
  }

  override update(time: number, delta: number): void {
    if (this._completed || !this._module?.update) return;
    try {
      this._module.update(time, delta);
    } catch (err) {
      console.error(`[MinigameScene] crash in ${this._minigameId}.update():`, err);
      this._forceComplete("cancelled", String(err));
    }
  }

  private _onComplete(result: string, delta?: MinigameDelta): void {
    if (this._completed) return;
    this._completed = true;
    this._shutdown();
    this._bus.emit("minigame.complete", { id: this._minigameId, result, delta });
  }

  private _forceComplete(result: string, error: string): void {
    if (this._completed) return;
    this._completed = true;
    this._bus.emit("minigame.error", { id: this._minigameId, error });
    this._shutdown();
    this._bus.emit("minigame.complete", { id: this._minigameId, result });
  }

  private _shutdown(): void {
    if (this._unsubComplete) {
      this._unsubComplete();
      this._unsubComplete = null;
    }

    if (this._module?.destroy) {
      try {
        this._module.destroy();
      } catch (err) {
        console.error(`[MinigameScene] crash in ${this._minigameId}.destroy():`, err);
      }
    }
    this._module = null;

    if (this._watchdog) {
      this._watchdog.remove();
      this._watchdog = null;
    }

    if (this._host) {
      this._host.destroy();
      this._host = null;
    }

    this.scene.resume("GameScene");
    this.scene.stop("MinigameScene");
  }
}
